import React, { useEffect, useState } from 'react';
import { api, fmtMoney } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { useData } from '../../context/DataContext';
import { Users, TrendingUp, FileText, Mail, BarChart3, GraduationCap, ClipboardList, XCircle, ShoppingBag } from 'lucide-react';

const Dashboard = ({ lob }) => {
  const darkMode = useDarkMode();
  const { lobs } = useData();
  const [summary, setSummary] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [mentorStats, setMentorStats] = useState(null);

  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const mentorLob = lobs?.find((l) => l.key === 'mentor') || null;

  useEffect(() => {
    if (!lob) return;
    setLoading(true); setErr('');
    Promise.all([
      api.get(`/reports/dashboard?lob_id=${lob.id}`).then((r) => r.data),
      api.get(`/contacts?lob_id=${lob.id}`).then((r) => r.data),
      api.get(`/deals?lob_id=${lob.id}`).then((r) => r.data),
      api.get(`/reports/pipeline?lob_id=${lob.id}`).then((r) => r.data),
    ]).then(([s, contacts, deals, pl]) => {
      setSummary(s);
      setRecentContacts(contacts.slice(0, 8));
      setRecentDeals(deals.slice(0, 8));
      setPipeline(pl);
    }).catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [lob?.id]);

  // Spotlight card: only fetched (and only shown) when the user is looking at
  // a different LOB tab — avoids showing the exact same numbers twice when
  // they're already on the Real Estate Mentorship tab itself.
  useEffect(() => {
    if (!mentorLob || mentorLob.id === lob?.id) { setMentorStats(null); return; }
    api.get(`/reports/dashboard?lob_id=${mentorLob.id}`).then((r) => setMentorStats(r.data)).catch(() => setMentorStats(null));
  }, [mentorLob?.id, lob?.id]);

  if (!lob) return null;

  return (
    <>
      <div className="page-toolbar" data-testid="dashboard-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Dashboard</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
      </div>

      {err && <div className="module-banner err" data-testid="dashboard-error">{err}</div>}

      <div className="content-area" data-testid="dashboard-content">
        {/* KPI Grid */}
        <div className="kpi-grid">
          <KPI icon={<Users size={12} />} label="Contacts" value={summary?.contacts ?? '—'} />
          <KPI icon={<TrendingUp size={12} />} label="Active deals" value={summary?.deals ?? '—'} />
          <KPI icon={<BarChart3 size={12} />} label="Pipeline value" value={summary ? fmtMoney(summary.pipeline_value) : '—'} />
          <KPI icon={<FileText size={12} />} label="Documents" value={summary?.documents ?? '—'} />
          <KPI icon={<Mail size={12} />} label="Campaigns" value={summary?.campaigns ?? '—'} />
          <KPI icon={<ClipboardList size={12} />} label="Applications this month" value={summary?.monthly_applications ?? '—'} testId="kpi-monthly-applications" />
          <KPI icon={<XCircle size={12} />} label="Lost deals" value={summary?.lost_deals ?? '—'} testId="kpi-lost-deals" />
          <KPI icon={<ShoppingBag size={12} />} label="Products sold" value={summary ? `${summary.sold_products_count} · ${fmtMoney(summary.sold_products_revenue)}` : '—'} testId="kpi-products-sold" />
        </div>

        {/* Stage strip from /reports/pipeline */}
        <div className="stage-strip" data-testid="dashboard-stage-strip">
          {(pipeline?.by_stage || []).map((s, i) => {
            const total = pipeline?.kpis?.total_pipeline || 1;
            const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
            return (
              <div className="ss-card" key={s.key || i}>
                <div className="ss-name">{s.label}</div>
                <div className="ss-val">{fmtMoney(s.value)}</div>
                <div className="ss-count">{s.count} deals</div>
                <div className="ss-ind"><div className="ss-ind-fill" style={{ width: `${pct}%`, background: lob.accent }} /></div>
              </div>
            );
          })}
        </div>

        {mentorStats && (
          <div className="card" data-testid="mentorship-spotlight-card" style={{ borderLeft: `3px solid ${mentorLob.accent}` }}>
            <div className="card-hd" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GraduationCap size={13} style={{ color: mentorLob.accent }} />
              Real Estate Mentorship
              <span className="card-sub">quick glance</span>
            </div>
            <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <KPI icon={<Users size={12} />} label="Mentorship signups" value={mentorStats.contacts ?? '—'} />
              <KPI icon={<BarChart3 size={12} />} label="Enrollment pipeline value" value={fmtMoney(mentorStats.pipeline_value)} />
            </div>
          </div>
        )}

        <div className="mid-grid">
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-hd">Recent contacts <span className="card-sub">{recentContacts.length}</span></div>
            {recentContacts.length === 0 ? (
              <div className="empty-state"><div className="ttl">No contacts yet</div><div className="sub">Use the Contacts tab to add your first.</div></div>
            ) : recentContacts.map((c) => (
              <div className="contact-row" key={c.id} data-testid={`dash-contact-${c.id}`}>
                <div className="init-circle" style={{ background: aBg, color: aTc }}>{(c.first?.[0] || '') + (c.last?.[0] || '')}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{c.first} {c.last}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-faded)' }}>{c.company || c.email}</div>
                </div>
                {c.amount && <span style={{ fontSize: 11, fontWeight: 600 }}>{fmtMoney(c.amount)}</span>}
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-hd">Recent deals <span className="card-sub">{recentDeals.length}</span></div>
            {recentDeals.length === 0 ? (
              <div className="empty-state"><div className="ttl">No deals yet</div><div className="sub">Open the Pipeline tab to add deals.</div></div>
            ) : recentDeals.map((d) => (
              <div className="action-row" key={d.id} data-testid={`dash-deal-${d.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-faded)' }}>{d.type || '—'} · stage: {d.stage}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600 }}>{fmtMoney(d.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="module-banner">Loading…</div>}
    </>
  );
};

const KPI = ({ icon, label, value, testId }) => (
  <div className="kpi-card" data-testid={testId}>
    <div className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{icon}{label}</div>
    <div className="kpi-value">{value}</div>
  </div>
);

export default Dashboard;
